// services/courierPerformance.ts — skor performa kurir Mengantar per kota (cache sesi)
import { getApiUrl, API_KEY } from './apiConfig';
export interface CourierPerf { scores: Record<string, number>; best: string | null; recommended: string | null }
// BATAS FATAL (aturan user 2026-09-29): ekspedisi dengan performa DI BAWAH ini
// DILARANG dipilih di menu /pesanan (ganti kurir, booking Everpro, tarif manual-auto).
export const COURIER_MIN_PERFORMANCE = 80;
// GATE 2 (aturan user 2026-10-01): kalau tidak ada yang ≥ 80%, tampilkan HANYA
// yang ≥ 70%. Di bawah 70% = tidak tampil di customer (CS handle manual).
export const COURIER_MID_PERFORMANCE = 70;
// Pulau Jawa = kode pos awalan 1/4/5/6 (DKI+Banten 1x, Jabar 15-18/40-46,
// Jateng+DIY 50-55, Jatim 60-69). Luar Jawa = 2/3/7/8/9. Aturan user 2026-09-30:
// di Jawa tampilkan SEMUA kurir aktif (di bawah 80% pun ikut tampil).
export function isJavaPostal(postal?: string | null): boolean {
    const d = String(postal || '').replace(/\D/g, '');
    return d.length >= 5 && /^[1456]/.test(d);
}
// true hanya kalau skor DIKETAHUI dan < ambang. Skor null/tidak ada data = lolos
// (tidak bisa dinilai) — tapi UI tetap menampilkan tanpa badge.
export function isLowPerfCourier(perf: unknown, minHigh: number = COURIER_MIN_PERFORMANCE): boolean {
    return typeof perf === 'number' && perf > 0 && perf < minHigh;
}
// Aturan pilih FATAL (2026-09-29): WAJIB yang performanya jelas ≥ ambang setting.
// - perf diketahui → harus >= ambang, kalau tidak → TIDAK boleh dipilih.
// - perf tidak diketahui (null) → boleh HANYA kalau data skor kota tidak tersedia
//   (scoresAvailable=false, mis. API Mengantar gagal). Kalau data kota ADA tapi
//   kurir ini tanpa skor → ikut diblokir (tidak memenuhi "wajib ada performanya").
export function isCourierSelectable(perf: unknown, scoresAvailable: boolean, minHigh: number = COURIER_MIN_PERFORMANCE): boolean {
    if (typeof perf === 'number' && perf > 0) return perf >= minHigh;
    return !scoresAvailable;
}
const mem = new Map<string, { ts: number; data: CourierPerf }>();
const inflight = new Map<string, Promise<CourierPerf | null>>();
const TTL = 6 * 60 * 60 * 1000;
// Kota sampah dari ai_city gagal analisis (SKIPPED/ERROR/circuit breaker) —
// JANGAN tembak backend (bukti 2026-10-07: city=SKIPPED → upstream timeout).
// 2026-10-29 (/pesanan berat, N+1 20 req): ai_city kadang berisi alamat mentah
// ("Patokan: Laundry Khodijah (usaha milik penerima).") → tolak tanpa fetch.
const INVALID_CITY_RE = /^(SKIPPED|ERROR|RE-ANALYZE|CIRCUIT BREAKER)/i;
// Nama kota asli max ~35 char, tanpa ':'/'('/')/digit-alamat. Lebih dari itu = alamat mentah.
export function isPlausibleCity(city?: string | null): boolean {
  const c = String(city || '').trim();
  if (!c || c.length > 35) return false;
  if (INVALID_CITY_RE.test(c) || /circuit breaker/i.test(c)) return false;
  if (/[:();]/.test(c)) return false;
  if (/\b(patokan|laundry|jl\.?|jalan|rt\b|rw\b|blok|dusun|gang|no\.\s*\d)/i.test(c)) return false;
  return true;
}
export function perfKeyFor(name?: string): string | null {
  const n = String(name || '').toLowerCase();
  if (n.includes('jne')) return 'JNE';
  if (n.includes('sicepat')) return 'SiCepat';
  if (n.includes('sap')) return 'Sap';
  if (n.includes('id express') || n.includes('idexpress')) return 'iDexpress';
  if (n === 'jt' || n.includes('j&t') || n.includes('jnt')) return 'JT';
  if (n.includes('lion')) return 'lion';
  if (n.includes('anteraja')) return 'anteraja';
  if (n.includes('spx')) return 'spx';
  if (n.includes('pos')) return 'pos';
  return null;
}
export async function fetchCourierPerformance(city: string, brandId?: string): Promise<CourierPerf | null> {
  if (!isPlausibleCity(city)) return null;
  const k = city.trim().toUpperCase();
  const hit = mem.get(k);
  if (hit && Date.now() - hit.ts < TTL) return hit.data;
  const running = inflight.get(k);
  if (running) return running;
  // Sekali retry (jeda 1,5s): skor gagal sesaat = fail-open (daftar tampil semua,
  // customer bisa ambil termurah yang buruk — insiden ID Express 2026-10-02).
  // brandId → backend coba varian AUTH Mengantar (akurat per rute, whitelist-only),
  // gagal/403 otomatis fallback ke publik.
  const p = (async () => {
  for (let attempt = 0; attempt < 2; attempt++) {
    if (attempt > 0) await new Promise(r => setTimeout(r, 1500));
    try {
      const q = `/api/shipping/courier-performance?city=${encodeURIComponent(city.trim())}${brandId ? `&brandId=${encodeURIComponent(brandId)}` : ''}`;
      const res = await fetch(`${getApiUrl()}${q}`, { headers: API_KEY ? { 'x-api-key': API_KEY } : {} });
      if (!res.ok) continue;
      const json = await res.json();
      if (!json?.success || !json?.data?.scores) continue;
      mem.set(k, { ts: Date.now(), data: json.data });
      return json.data as CourierPerf;
    } catch { /* coba sekali lagi */ }
  }
  return null;
  })();
  inflight.set(k, p);
  try { return await p; } finally { inflight.delete(k); }
}
